import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert } from "lucide-react";
import {
  buildEnrichedFindings,
  ENRICHMENT_CAP,
  type EnrichmentInput,
} from "@/reports/telecom/findingsEnrichment";

interface Props {
  findings: EnrichmentInput[];
  /** Only render for telecom_v2; caller controls gating. */
  className?: string;
}

const RISK_COLORS: Record<string, string> = {
  Critical: "bg-red-700 text-white",
  CRITICAL: "bg-red-700 text-white",
  High: "bg-destructive text-destructive-foreground",
  HIGH: "bg-destructive text-destructive-foreground",
};

export const EnrichedFindingsPanel: React.FC<Props> = ({ findings, className }) => {
  const enriched = useMemo(() => buildEnrichedFindings(findings, ENRICHMENT_CAP), [findings]);

  if (!enriched.items.length) return null;

  return (
    <section className={`mt-6 ${className ?? ""}`}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-destructive" />
          Top Findings — Executive View
        </h3>
        <span className="text-xs text-muted-foreground">
          {enriched.capped
            ? `Showing top ${enriched.cap} of ${enriched.totalEligible} Critical/High findings`
            : `${enriched.items.length} Critical/High finding${enriched.items.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        Narrative view of the highest-priority findings. The structured findings table above
        remains the source of record.
      </p>

      <div className="space-y-3">
        {enriched.items.map((f) => (
          <article
            key={`${f.index}-${f.control}`}
            className="rounded-lg border bg-card p-4 shadow-sm"
          >
            <header className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs font-mono text-muted-foreground">#{f.index}</span>
              <span className="text-xs font-mono text-muted-foreground">{f.control}</span>
              <span className="text-xs text-muted-foreground">· {f.domain}</span>
              <Badge className={`${RISK_COLORS[f.risk] ?? "bg-muted"} text-[10px] px-1.5 py-0 ml-auto`}>
                {f.risk}
              </Badge>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {f.priority}
              </Badge>
            </header>

            <h4 className="text-sm font-semibold text-foreground mb-2">{f.title}</h4>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div className="md:col-span-2">
                <dt className="font-semibold text-muted-foreground uppercase tracking-wide">
                  Why it matters
                </dt>
                <dd className="text-foreground/90 mt-0.5">{f.whyItMatters}</dd>
              </div>
              <div>
                <dt className="font-semibold text-muted-foreground uppercase tracking-wide">
                  Recommended action
                </dt>
                <dd className="text-foreground/90 mt-0.5 whitespace-pre-wrap">{f.recommendedAction}</dd>
              </div>
              <div>
                <dt className="font-semibold text-muted-foreground uppercase tracking-wide">
                  Supporting measures
                </dt>
                <dd className="text-foreground/90 mt-0.5 whitespace-pre-wrap">{f.supportingMeasures}</dd>
              </div>
              <div>
                <dt className="font-semibold text-muted-foreground uppercase tracking-wide">
                  Time horizon
                </dt>
                <dd className="text-foreground/90 mt-0.5">{f.timeHorizon}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
};
